import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  PageHeader,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  notification,
} from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';
import BackButton from '../../../components/common/BackButton';
import Spinner from '../../../components/common/Spinner';
import {
  getSellerCategories,
  getVolumeOfPotential,
  getYardTypes,
} from '../../../app/store/commonSlice';
import {
  useAddProfileMutation,
  useGetMyProfileQuery,
  useUpdateProfileMutation,
} from '../../../app/api/profileApiSlice';
import Seller from '../../../core/models/Seller';

const { Text } = Typography;

const Profile: React.FC = () => {
  const [form] = Form.useForm();

  const { data: profile, isLoading } = useGetMyProfileQuery();
  const [addProfile, { isLoading: addProfileLoading }] = useAddProfileMutation();
  const [updateProfile, { isLoading: updateProfileLoading }] = useUpdateProfileMutation();

  const sellerCategories = useSelector(getSellerCategories);
  const yardTypes = useSelector(getYardTypes);
  const volumeOfPotential = useSelector(getVolumeOfPotential);

  const handleUpdateProfile = (values: any): void => {
    // let updatedSeller: Seller = profile ?? {};

    // if (profile) Object.create(updatedSeller, profile);

    // if (profile) updatedSeller = Object.assign(updatedSeller, profile);
    // // else updatedSeller = values;

    let updatedSeller: Seller;

    if (profile) {
      updatedSeller = { ...profile };
      Object.assign(updatedSeller, values);
    } else {
      updatedSeller = values;
    }

    if (profile?.id) {
      updateProfile(updatedSeller)
        .unwrap()
        .then(() => {
          notification.success({
            message: 'Update Profile',
            description: 'Profile updated successfully',
          });
        })
        .catch((error: any) => {
          console.log(error);
        });
    } else {
      addProfile(updatedSeller)
        .unwrap()
        .then(() => {
          notification.success({
            message: 'Create Profile',
            description: 'Profile created successfully, please wait for approval',
          });
        })
        .catch((error: any) => {
          console.log(error);
        });
    }
  };

  const ProfileStatus: React.FC = () => <Tag color='magenta'>{profile?.status.name}</Tag>;

  return (
    <>
      <BackButton />
      <PageHeader
        title='Profile'
        subTitle={
          <>
            {!profile && <Text>Please complete your profile to continue.</Text>}
            {profile && <ProfileStatus />}
          </>
        }
      >
        {profile && profile?.status?.id === 1 && (
          <Space direction='vertical' style={{ width: '100%', padding: '0px 0px 10px 0px' }}>
            <Alert
              message='Your profile is pending for approval. Please contact AutoStream for more details.'
              type='warning'
            />
          </Space>
        )}
        {isLoading ? (
          <Spinner />
        ) : (
          <Form
            id='updateVehicleForm'
            form={form}
            layout='vertical'
            initialValues={{
              ...profile,
              sellerCategoryId: profile?.sellerCategory?.id,
              yardTypeId: profile?.yardType?.id,
              volumeOfPotentialId: profile?.volumeOfPotential?.id,
            }}
            onFinish={handleUpdateProfile}
            disabled={profile?.id != null}
          >
            <Divider orientation='left' orientationMargin='0'>
              Company Information
            </Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name='sellerCategoryId'
                  label='Seller Category'
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Select>
                    {sellerCategories?.map((category) => (
                      <Select.Option key={category.id} value={category.id} initialValues={true}>
                        {category.category}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                {/* <Form.Item
                  name='personInCharge'
                  label='Name'
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder='Name' />
                </Form.Item> */}
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name='companyName' label='Company Name'>
                  <Input placeholder='Company Name' />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name='representativePersonName' label='Representative Person Name'>
                  <Input placeholder='Representative Person Name' />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name='phoneNumber'
                  label='Phone Number'
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder='Phone Number' />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name='addressLine1'
                  label='Address 1'
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder='Address Line 1' />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name='addressLine2' label='Address 2'>
                  <Input placeholder='Address Line 2' />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name='city'
                  label='City'
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder='City' />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name='postalCode'
                  label='Postal Code'
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder='Postal Code' />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name='yardTypeId' label='Yard Preference'>
                  <Select>
                    {yardTypes?.map((type) => (
                      <Select.Option key={type.id} value={type.id} initialValues={true}>
                        {type.type}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name='volumeOfPotentialId' label='Monthly Selling Volume'>
                  <Select>
                    {volumeOfPotential?.map((volume) => (
                      <Select.Option key={volume.id} value={volume.id} initialValues={true}>
                        {volume.volume}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation='left' orientationMargin='0'>
              Bank Information
            </Divider>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['bankInformation', 'accountNumber']}
                  label='Account Number'
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder='Account Number' />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['bankInformation', 'bankName']}
                  label='Bank Name'
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder='Bank Name' />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['bankInformation', 'bankBranch']}
                  label='Bank Branch'
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <Input placeholder='Bank Branch' />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item>
              <Button
                type='primary'
                htmlType='submit'
                loading={addProfileLoading || updateProfileLoading}
              >
                Update
              </Button>
            </Form.Item>
          </Form>
        )}
      </PageHeader>
    </>
  );
};

export default Profile;
